import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useOnboardingFieldsQuery } from '@/features/auth/api/useAuthMutations';
import { fieldKey, normalizeFieldItem } from '@/features/auth/utils/onboardingFields';
import {
  KYC,
  type FieldItem,
  type NormalizedField,
} from '@/features/auth/types/onboardingFields';
import { useProfileOverviewForm, scrollToProfileField } from './overview/useProfileOverviewForm';
import { useProfileOverviewKyc } from './overview/useProfileOverviewKyc';
import { useProfilePhotoUpload } from './overview/useProfilePhotoUpload';
import { validateSelectedField } from './overview/validateSelectedFields';
import { ProfileBanner } from './overview/ProfileBanner';
import { PersonalInfoSection } from './overview/PersonalInfoSection';
import { AddressSection } from './overview/AddressSection';
import { OtherDetailsSection } from './overview/OtherDetailsSection';
import { SaveBar } from './overview/SaveBar';

export function OverviewTab() {
  const {
    profile,
    user,
    editing,
    setEditing,
    form,
    setForm,
    dynamicForm,
    setDynamicForm,
    savedDynamicForm,
    nameErrors,
    clearNameError,
    isSaving,
    validateRequiredNames,
    handleSave,
    handleCancel,
  } = useProfileOverviewForm();

  const {
    fileInputRef,
    preview: photoPreview,
    busy: photoBusy,
    openPicker,
    handleFileChange,
  } = useProfilePhotoUpload(user);

  const userRole = localStorage.getItem('role') || user?.role || null;
  const fieldsQuery = useOnboardingFieldsQuery(userRole);
  const selectedFields = fieldsQuery.data?.data?.selectedFields || [];
  const kycStatus = fieldsQuery.data?.data?.kycStatus;

  const visibleFields: NormalizedField[] = selectedFields
    .map((item: FieldItem) => normalizeFieldItem(item))
    .filter((f: NormalizedField | null): f is NormalizedField => f !== null && f.visible);

  const kyc = useProfileOverviewKyc({
    dynamicForm,
    setDynamicForm,
    visibleFields,
    kycStatus,
    savedDetails: savedDynamicForm,
    editing,
    userId: user?._id,
  });

  const onSave = () => {
    if (!validateRequiredNames()) return;

    const nextErrors: Record<string, string> = {};
    for (const field of visibleFields) {
      const rawVal = user?.dynamicFields?.[field.fieldName];
      if (
        rawVal &&
        typeof rawVal === 'object' &&
        (rawVal as Record<string, unknown>).url
      ) {
        continue;
      }

      const current = String(dynamicForm[field.fieldName] || '').trim();
      const saved = String(savedDynamicForm[field.fieldName] || '').trim();
      const key = fieldKey(field.fieldName);
      const isPanOrGst = key === KYC.PAN || key === KYC.GST;

      // PAN/GST: validate only when the value actually changed.
      if (isPanOrGst && current === saved) {
        kyc.clearFieldError(field.fieldName);
        continue;
      }

      const message = validateSelectedField(
        field,
        dynamicForm[field.fieldName] || '',
      );
      if (message) {
        nextErrors[field.fieldName] = message;
        kyc.setFieldError(field.fieldName, message);
      } else {
        kyc.clearFieldError(field.fieldName);
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      const firstErrorField = Object.keys(nextErrors)[0];
      scrollToProfileField(`profile-dynamic-${firstErrorField}`);
      return;
    }

    if (kyc.pendingVerification) {
      toast.error(
        kyc.pendingVerifyMessage ||
          'Please verify PAN / GST before saving',
      );
      return;
    }

    handleSave();
  };

  if (profile.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-brand)]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <ProfileBanner
        user={user}
        form={form}
        editing={editing}
        onToggleEdit={() => setEditing(!editing)}
        photoPreview={photoPreview}
        photoBusy={photoBusy}
        fileInputRef={fileInputRef}
        onOpenPhotoPicker={openPicker}
        onPhotoFileChange={handleFileChange}
      />

      <PersonalInfoSection
        form={form}
        editing={editing}
        setForm={setForm}
        nameErrors={nameErrors}
        onClearNameError={clearNameError}
      />

      <AddressSection form={form} editing={editing} setForm={setForm} />

      <OtherDetailsSection
        visibleFields={visibleFields}
        user={user}
        dynamicForm={dynamicForm}
        editing={editing}
        setDynamicForm={setDynamicForm}
        fieldErrors={kyc.fieldErrors}
        onClearFieldError={kyc.clearFieldError}
        shouldShowVerifyButton={kyc.shouldShowVerifyButton}
        isKycField={kyc.isKycField}
        isFieldLocked={kyc.isFieldLocked}
        unlockVerifiedField={kyc.unlockVerifiedField}
        isVerifyDisabled={kyc.isVerifyDisabled}
        onVerifyPan={(name) => void kyc.verifyPanNumber(name)}
        onVerifyGst={(name) => void kyc.verifyGstNumber(name)}
        verifyPanPending={kyc.verifyPanPending}
        verifyGstPending={kyc.verifyGstPending}
        panRows={kyc.panRows}
        gstRows={kyc.gstRows}
      />

      {editing ? (
        <SaveBar
          isSaving={isSaving || kyc.verifyPanPending || kyc.verifyGstPending}
          onCancel={handleCancel}
          onSave={onSave}
        />
      ) : null}
    </div>
  );
}
